<template>
  <!-- A button, or a link when `href` is given, with only an icon. Its name shows in a small tooltip
       on hover or keyboard focus (and is its accessible name always), so a toolbar stays compact. -->
  <component :is="href ? 'a' : 'button'" :type="href ? undefined : 'button'" :href="href" :target="href ? '_blank' : undefined"
    :rel="href ? 'noopener noreferrer' : undefined" :data-turbo="href ? 'false' : undefined"
    :aria-label="label" :aria-pressed="pressed === null ? undefined : pressed" :disabled="href ? undefined : disabled"
    class="group/icon relative inline-flex items-center justify-center flex-shrink-0 rounded-lg border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 disabled:opacity-50 disabled:pointer-events-none"
    :class="[SIZES[size], TONES[tone]]">
    <svg :class="ICON_SIZES[size]" fill="none" stroke="currentColor" :stroke-width="tone === 'primary' ? 2 : 1.7" viewBox="0 0 24 24" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" :d="icon"/>
    </svg>
    <span v-if="!hideTip" role="tooltip"
      class="pointer-events-none absolute top-full z-40 mt-1.5 px-2 py-1 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-medium whitespace-nowrap shadow-lg opacity-0 transition-opacity group-hover/icon:opacity-100 group-hover/icon:delay-300 group-focus-visible/icon:opacity-100"
      :class="ALIGN[align]">{{ label }}</span>
  </component>
</template>

<script setup>
defineProps({
  icon: { type: String, required: true }, // path data
  label: { type: String, required: true },
  href: { type: String, default: null }, // makes it a link that opens in a new tab
  tone: { type: String, default: 'default' }, // default | active | success | primary | plain
  size: { type: String, default: 'md' }, // sm (24px, for a line of text) | md (32px) | lg (36px)
  pressed: { type: Boolean, default: null }, // for a button that opens or closes something, or is on or off
  disabled: { type: Boolean, default: false },
  align: { type: String, default: 'center' }, // where the tooltip sits under the button: center | right | left
  hideTip: { type: Boolean, default: false }, // e.g. while the thing it opens is showing
})

const SIZES = { sm: 'w-6 h-6', md: 'w-8 h-8', lg: 'w-9 h-9' }
const ICON_SIZES = { sm: 'w-3.5 h-3.5', md: 'w-[18px] h-[18px]', lg: 'w-[18px] h-[18px]' }
const TONES = {
  default: 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white',
  active: 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
  success: 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30',
  primary: 'border-transparent bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
  plain: 'border-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white',
}
const ALIGN = { center: 'left-1/2 -translate-x-1/2', right: 'right-0', left: 'left-0' }
</script>
